import React, { useCallback, useEffect, useState } from "react";
import cn from "classnames";
import styles from "./alarm-profile-card.module.scss";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import radioButtonInactive from "@src/assets/svg/radio-button-inactive.svg";
import radioButtonActive from "@src/assets/svg/radio-button-active.svg";
import ellipsisHorizontalCircle from "@src/assets/svg/ellipsis-horizontal-circle.svg";
import { AlarmProfilePopover } from "@src/components/atoms/alarm-profile-popover/alarm-profile-popover";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { Button } from "@src/components/atoms/buttons/button/button";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import warningTriangleIcon from "@src/assets/svg/warning-triangle.svg";
import { AlarmProfile } from "@src/cloud-connect/alarms/entities/alarm-profile";
import { getAlarmProfiles } from "@src/cloud-connect/dependency-injection-container/get-alarm-profiles";
import { useI18N } from "@src/language/language-context";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { RenderIfUserHasPermission } from "../render-if-user-has-permission/render-if-user-has-permission";

const cssClassName = "alarm-profile-card";

interface AlarmProfileCardPropsTypes {
  alarmProfile: AlarmProfile;
  activateProfile: (id: string) => void;
}

interface ReadAlarmProfileCardPropsTypes extends AlarmProfileCardPropsTypes {
  setLocalCardState: (state: "read" | "edit" | "delete") => void;
}

const ReadAlarmProfileCard = ({
  alarmProfile,
  activateProfile,
  setLocalCardState,
}: ReadAlarmProfileCardPropsTypes) => {
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const returnIcon = useCallback(() => {
    return alarmProfile.getActive() ? radioButtonActive : radioButtonInactive;
  }, [alarmProfile]);
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  return (
    <>
      <FlexRowContainer justifyContent="space-between">
        <FlexRowContainer>
          <IconButton
            icon={returnIcon()}
            fun={() => activateProfile(alarmProfile.getId())}
            variant={"no-padding"}
            disabled={
              authContext &&
              !authContext.permissions.includes("manage-condition-monitoring")
            }
          />
          <p
            className={cn(styles[`${cssClassName}__title`], {
              [styles[`${cssClassName}__title--active`]]:
                alarmProfile.getActive(),
            })}
          >
            {alarmProfile.getName()}
          </p>
        </FlexRowContainer>
        <RenderIfUserHasPermission permission="manage-condition-monitoring">
          <div className={styles[`${cssClassName}__popover-wrapper`]}>
            <IconButton
              icon={ellipsisHorizontalCircle}
              fun={() => setPopoverIsOpen(!popoverIsOpen)}
              variant={"no-padding"}
            />
            {popoverIsOpen && (
              <AlarmProfilePopover
                profileId={alarmProfile.getId()}
                setLocalCardState={setLocalCardState}
                setPopoverIsOpen={setPopoverIsOpen}
              />
            )}
          </div>
        </RenderIfUserHasPermission>
      </FlexRowContainer>
      <p className={styles[`${cssClassName}__description`]}>
        {alarmProfile.getDescription()}
      </p>
    </>
  );
};

const EditAlarmProfileCard = ({
  alarmProfile,
  setLocalCardState,
}: Partial<ReadAlarmProfileCardPropsTypes>) => {
  const [nameValue, setNameValue] = useState(alarmProfile.getName());
  const [descriptionValue, setDescriptionValue] = useState(
    alarmProfile.getDescription(),
  );
  const i18n = useI18N();

  const texts = {
    save: i18n("modules.alarmProfileCard.save"),
    cancel: i18n("modules.alarmProfileCard.cancel"),
  };

  const saveCard = () => {
    alarmProfile.setName(nameValue);
    alarmProfile.setDescription(descriptionValue);
    alarmProfile.setJustCreated(false);
    setLocalCardState("read");
  };

  const handleCancel = () => {
    if (alarmProfile.getJustCreated()) {
      getAlarmProfiles().deleteAlarmProfile(alarmProfile.getId());
    } else {
      setLocalCardState("read");
    }
  };

  useEffect(() => {
    setNameValue(alarmProfile.getName());
    setDescriptionValue(alarmProfile.getDescription());
  }, [alarmProfile]);

  return (
    <>
      <FlexColumnContainer>
        <input
          type="text"
          value={nameValue}
          onChange={(e) => setNameValue(e.target.value)}
          className={styles[`${cssClassName}__input`]}
        />
        <textarea
          maxLength={85}
          value={descriptionValue}
          onChange={(e) => setDescriptionValue(e.target.value)}
          className={styles[`${cssClassName}__textarea`]}
        />
        <FlexRowContainer>
          <Button fun={saveCard} text={texts.save} variant={"full-width"} />
          <Button
            fun={handleCancel}
            text={texts.cancel}
            variant={["secondary", "full-width"]}
          />
        </FlexRowContainer>
      </FlexColumnContainer>
    </>
  );
};

const DeleteAlarmProfileCard = ({
  alarmProfile,
  setLocalCardState,
}: Partial<ReadAlarmProfileCardPropsTypes>) => {
  const deleteCard = (id) => {
    getAlarmProfiles().deleteAlarmProfile(id);
  };

  const i18n = useI18N();

  const texts = {
    save: i18n("modules.alarmProfileCard.save"),
    cancel: i18n("modules.alarmProfileCard.cancel"),
    delete: i18n("modules.alarmProfileCard.delete"),
    warning: i18n("modules.alarmProfileCard.warning"),
    warningIconAltText: i18n("modules.alarmProfileCard.warningIconAltText"),
  };

  return (
    <>
      <FlexColumnContainer verticalGap={16}>
        <FlexRowContainer>
          <img
            src={warningTriangleIcon}
            alt={texts.warningIconAltText}
            className={styles[`${cssClassName}__warning-icon`]}
          />
          <span className={styles[`${cssClassName}__warning-text`]}>
            {texts.warning}
          </span>
        </FlexRowContainer>
        <FlexColumnContainer>
          <SubTitle text={alarmProfile.getName()} />
          <p
            className={cn(
              styles[`${cssClassName}__description`],
              styles[`${cssClassName}__description--white`],
            )}
          >
            {alarmProfile.getDescription()}
          </p>
        </FlexColumnContainer>
        <FlexRowContainer>
          <Button
            fun={() => setLocalCardState("read")}
            text={texts.cancel}
            variant={["secondary", "full-width"]}
          />
          <Button
            fun={() => deleteCard(alarmProfile.getId())}
            text={texts.delete}
            variant={["full-width", "alert", "secondary"]}
          />
        </FlexRowContainer>
      </FlexColumnContainer>
    </>
  );
};

const returnAlarmProfileCard = (
  localCardState,
  alarmProfile,
  activateProfile,
  setLocalCardState,
) => {
  switch (localCardState) {
    case "read":
      return (
        <ReadAlarmProfileCard
          alarmProfile={alarmProfile}
          activateProfile={activateProfile}
          setLocalCardState={setLocalCardState}
        />
      );
    case "edit":
      return (
        <EditAlarmProfileCard
          alarmProfile={alarmProfile}
          setLocalCardState={setLocalCardState}
        />
      );
    case "delete":
      return (
        <DeleteAlarmProfileCard
          alarmProfile={alarmProfile}
          setLocalCardState={setLocalCardState}
        />
      );
    default:
      return (
        <ReadAlarmProfileCard
          alarmProfile={alarmProfile}
          activateProfile={activateProfile}
          setLocalCardState={setLocalCardState}
        />
      );
  }
};

export const AlarmProfileCard = ({
  alarmProfile,
  activateProfile,
}: AlarmProfileCardPropsTypes) => {
  const [localCardState, setLocalCardState] = useState<
    "read" | "edit" | "delete"
  >("read");

  useEffect(() => {
    if (alarmProfile.getJustCreated()) {
      setLocalCardState("edit");
    }
  }, [alarmProfile]);

  return (
    <div
      className={cn(styles[cssClassName], {
        [styles[`${cssClassName}--active`]]: alarmProfile.getActive(),
        [styles[`${cssClassName}--edit`]]: localCardState === "edit",
        [styles[`${cssClassName}--delete`]]: localCardState === "delete",
      })}
    >
      {returnAlarmProfileCard(
        localCardState,
        alarmProfile,
        activateProfile,
        setLocalCardState,
      )}
    </div>
  );
};
